import React from 'react';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

interface MediaContact {
  id: number;
  name: string;
  role: string;
  department: string;
  email: string;
  phone: string;
  image: string;
  alt: string;
  availability: string;
  languages: string[];
}

const MediaContactSection: React.FC = () => {
  const mediaContacts: MediaContact[] = [
  {
    id: 1,
    name: 'Shija Mayala',
    role: 'Director of Communications',
    department: 'Global Media Relations',
    email: 'shija.mayala@noenemy.life',
    phone: '+255 763529460',
    image: "/assets/images/Shija_PassChina-1769631519261.jpg",
    alt: 'Shija Mayala, Director of Communications for SIG Day Global No Enemy Festival',
    availability: 'Available 24/7 for urgent inquiries',
    languages: ['English', 'Swahili']
  },
  {
    id: 2,
    name: 'Grace Shija',
    role: 'Research & Impact Spokesperson',
    department: 'Evidence & Analytics',
    email: 'grace.shija@noenemy.life',
    phone: '+255 763529460',
    image: "/assets/images/Grace_Photo-1771312214787.jpeg",
    alt: 'Grace Shija, Research and Impact Spokesperson for SIG Day movement',
    availability: 'Monday-Friday, 9 AM - 6 PM EAT',
    languages: ['English', 'Swahili']
  },
  {
    id: 3,
    name: 'Musa Emmanuel',
    role: 'Regional Media Coordinator',
    department: 'Africa & Middle East',
    email: 'musa.emmanuel@noenemy.life',
    phone: '+255 657487297',
    image: "/assets/images/Musa_Photo-1771256177357.jpeg",
    alt: 'Musa Emmanuel, Regional Media Coordinator for Africa and Middle East region',
    availability: 'Monday-Saturday, 8 AM - 8 PM EAT',
    languages: ['English', 'Swahili', 'French', 'Arabic']
  }];


  return (
    <section id="media-contact" className="py-20 bg-background">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 bg-primary/10 px-6 py-3 rounded-full mb-6">
            <Icon name="UserGroupIcon" size={24} className="text-primary" />
            <span className="text-primary font-cta text-sm font-bold">
              Media Contact Directory
            </span>
          </div>

          <h2 className="text-3xl lg:text-5xl font-headline font-bold text-foreground mb-6">
            Connect With Our Team
          </h2>

          <p className="text-lg text-muted-foreground max-w-3xl mx-auto font-body">
            Our media relations team is available to provide information, arrange interviews, and support your coverage of SIG Day (The Global No Enemy Festival) movement.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {mediaContacts.map((contact) =>
          <div
            key={contact.id}
            className="bg-card rounded-xl overflow-hidden border border-border transition-all duration-300 hover:shadow-xl hover:border-primary group">

              <div className="relative h-64 overflow-hidden bg-muted">
                <AppImage
                src={contact.image}
                alt={contact.alt}
                className={`w-full h-full transition-transform duration-500 group-hover:scale-110 ${
                  contact.id === 3 ? 'object-contain' : 'object-cover'
                }`} />

                <div className="absolute inset-0 bg-gradient-to-t from-foreground/80 to-transparent" />
                <div className="absolute bottom-4 left-4 right-4">
                  <h3 className="text-xl font-headline font-bold text-white mb-1">
                    {contact.name}
                  </h3>
                  <p className="text-sm text-white/90 font-body">{contact.role}</p>
                </div>
              </div>

              <div className="p-6">
                <div className="mb-4">
                  <span className="text-xs font-cta font-bold text-primary bg-primary/10 px-3 py-1 rounded-full">
                    {contact.department}
                  </span>
                </div>

                <div className="space-y-3 mb-4">
                  <div className="flex items-start space-x-3">
                    <Icon name="EnvelopeIcon" size={18} className="text-primary mt-0.5 flex-shrink-0" />
                    <a
                    href={`mailto:${contact.email}`}
                    className="text-sm text-foreground font-body hover:text-primary transition-colors duration-300 break-all">

                      {contact.email}
                    </a>
                  </div>

                  <div className="flex items-start space-x-3">
                    <Icon name="PhoneIcon" size={18} className="text-primary mt-0.5 flex-shrink-0" />
                    <a
                    href={`tel:${contact.phone}`}
                    className="text-sm text-foreground font-body hover:text-primary transition-colors duration-300">

                      {contact.phone}
                    </a>
                  </div>

                  <div className="flex items-start space-x-3">
                    <Icon name="ClockIcon" size={18} className="text-primary mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-muted-foreground font-body">
                      {contact.availability}
                    </span>
                  </div>
                </div>

                <div className="pt-4 border-t border-border">
                  <p className="text-xs text-muted-foreground font-body mb-2">Languages:</p>
                  <div className="flex flex-wrap gap-2">
                    {contact.languages.map((lang, index) =>
                  <span
                    key={index}
                    className="text-xs font-body text-foreground bg-muted px-2 py-1 rounded">

                        {lang}
                      </span>
                  )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="bg-gradient-to-br from-primary to-secondary rounded-xl p-8 lg:p-12 text-center">
          <Icon name="CalendarIcon" size={48} className="text-white mx-auto mb-6" />
          <h3 className="text-2xl lg:text-3xl font-headline font-bold text-white mb-4">
            Schedule an Interview
          </h3>
          <p className="text-lg text-white/90 mb-8 max-w-2xl mx-auto font-body">
            Request an interview with our founder, subject matter experts, or community representatives. We accommodate various time zones and formats.
          </p>
          <button className="px-8 py-4 bg-white text-primary rounded-lg font-cta text-base font-bold transition-all duration-300 hover:scale-105 hover:shadow-xl inline-flex items-center space-x-2">
            <Icon name="CalendarDaysIcon" size={20} />
            <span>Request Interview</span>
          </button>
        </div>
      </div>
    </section>);

};

export default MediaContactSection;